// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rapporter & indberetning's centre tile for a document leaf: one page of a
 * report. On a ruxd with the preview (contract gap 4, reusex-ufcm.7) that is
 * the server's own compiled page — the PNG of the Typst report the PDF is
 * built from — with page navigation across the sections, "Opdaterer…" while
 * a newer compile runs (the last pages stay up) and the section's Klar /
 * Ufuldstændig from the server. On an older ruxd (404) the page is composed
 * here from the live case (v8 `pageHtml`) and captioned "Forhåndsvisning —
 * PDF'en kan afvige".
 *
 * The server compiles one report, the Ressourcekortlægning; the
 * Miljøkortlægning's own Forside and Sagsdata (`report="miljoe"`) are
 * therefore always the composed ones, under its title. Its Prøver og bilag
 * (the `miljoe` page) spans the server's Miljøkortlægning and Prøver & bilag
 * sections, or composes both. The Indberetning page is the Materialeflow
 * over the pkt. 10 fractions (`IndberetningSheet`); the PDF page shows the
 * stored document itself (no leaf leads there any more).
 */

import { useEffect, useMemo, useState } from 'react';

import { api } from '../../../api/client';
import type { ReportPdfVersion } from '../../../api/types';
import type { ReportCoverState } from '../../../app/bygning/useReportCover';
import type { ReportPreviewState } from '../../../app/bygning/useReportPreview';
import type { CaseData } from '../../../app/bygning/useCaseData';
import type { ProjectRecord } from '../../../bygning/checklist';
import { MILJOE_NOTE, REPORT_TITLE, type ReportId } from '../../../bygning/rapportTree';
import { coverCaption, sectionOfPage, sectionRange } from '../../../bygning/reportPreview';
import {
  appendixRows,
  coverModel,
  demolitionSteps,
  PREVIEW_CAPTION,
  RECIPIENTS_NOTE,
  resourceSummaryLine,
  sagsdataRows,
  sampleRows,
  typeRows,
  type PageId,
  type PageModel,
} from '../../../bygning/pages';
import { versionTitle } from '../../../rapport/model';
import { EmptyState } from '../../EmptyState';
import { IconButton } from '../../IconButton';
import { Pill } from '../../Pill';
import { Spinner } from '../../Spinner';
import { TextLink } from '../../TextLink';
import { COVER_RENDER, CoverPickerDialog } from './CoverPicker';
import { IndberetningSheet } from './IndberetningSheet';
import styles from './PagePreview.module.css';

export interface PagePreviewProps {
  model: PageModel;
  /** Whose page: the Miljøkortlægning's Forside and Sagsdata are composed here. Default `ressource`. */
  report?: ReportId;
  data: CaseData;
  /** The project record with its Sagsdata fields (gap 3). */
  project: ProjectRecord | null;
  /** Re-read the case (the Indberetning page's "Prøv igen" when the fractions failed). */
  onRetry: () => void;
  /** ruxd's compiled preview; `mode: 'unsupported'` keeps the composed pages. */
  preview?: ReportPreviewState;
  /** The page resolution the preview was asked at. */
  ppi?: number;
  /** Paging past a section's last page selects the next section's card. */
  onSection?: (id: PageId) => void;
  /** The cover choice, for the Forside page's "Skift forsidebillede". */
  cover?: ReportCoverState;
  /** The Indberetning page's Materialeflow: a type opens it in Ressourcer. */
  onOpenType: (typeId: number) => void;
  /** The Materialeflow's Blokeret: "Det, der blokerer" (Gennemgang). */
  onShowBlocking: () => void;
}

/** The server sections a page spans: Prøver og bilag is both of the Miljøkortlægning's. */
function sectionsOf(id: PageId): PageId[] {
  return id === 'miljoe' ? ['miljoe', 'bilag'] : [id];
}

function Missing({ children = 'mangler' }: { children?: string }) {
  return <span className={styles.miss}>{children}</span>;
}

export function PagePreview({
  model,
  report = 'ressource',
  data,
  project,
  onRetry,
  preview,
  ppi,
  onSection,
  cover,
  onOpenType,
  onShowBlocking,
}: PagePreviewProps) {
  // The server's pages are the Ressourcekortlægning's: not the Miljøkortlægning's own front matter.
  const composedOnly = report === 'miljoe' && (model.id === 'forside' || model.id === 'sagsdata');
  if (model.id === 'indberetning') {
    return (
      <div className={styles.wrap}>
        <IndberetningSheet data={data} onRetry={onRetry} onOpenType={onOpenType} onShowBlocking={onShowBlocking} />
      </div>
    );
  }
  if (model.id === 'pdf') {
    return (
      <div className={styles.wrap}>
        <PdfSheet versions={data.reportVersions} />
      </div>
    );
  }
  if (!composedOnly && preview && preview.mode !== 'unsupported' && (preview.shown || !preview.error)) {
    return (
      <div className={styles.wrap}>
        <ServerPages model={model} preview={preview} ppi={ppi} onSection={onSection} cover={cover} />
      </div>
    );
  }
  return (
    <div className={styles.wrap}>
      {preview?.error && !composedOnly && (
        <p className={styles.reasons} role="alert">
          Serverens forhåndsvisning kunne ikke opbygges: {preview.error}{' '}
          <TextLink onClick={preview.refresh}>Prøv igen</TextLink>
        </p>
      )}
      <header className={styles.bar}>
        <span className={styles.caption}>{PREVIEW_CAPTION}</span>
        <Pill tone={model.ready ? 'good' : 'warn'}>{model.ready ? 'Klar' : 'Ufuldstændig'}</Pill>
      </header>
      {!model.ready && model.reasons.length > 0 && (
        <ul className={styles.reasons} aria-label="Mangler på siden">
          {model.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
      <article className={styles.paper} aria-label={`Forhåndsvisning af ${model.title}`}>
        <DocumentPage model={model} report={report} data={data} project={project} />
      </article>
    </div>
  );
}

/** The server's compiled pages: the selected section's first page, paged across the whole report. */
function ServerPages({
  model,
  preview,
  ppi,
  onSection,
  cover,
}: {
  model: PageModel;
  preview: ReportPreviewState;
  ppi?: number;
  onSection?: (id: PageId) => void;
  cover?: ReportCoverState;
}) {
  const shown = preview.shown;
  const count = shown?.pages.length ?? 0;
  const ids = sectionsOf(model.id);
  // The page's sections from the first's start to the last's end (the ones the server sent).
  const ranges = shown
    ? ids.flatMap((id) => {
        const r = sectionRange(shown.sections, id, count);
        return r ? [r] : [];
      })
    : [];
  const range =
    ranges.length > 0 ? { start: Math.min(...ranges.map((r) => r.start)), end: Math.max(...ranges.map((r) => r.end)) } : null;
  const [page, setPage] = useState(range?.start ?? 0);
  const [picking, setPicking] = useState(false);
  // A new section (or a new compile) lands on the section's first page,
  // unless the page already shown belongs to it.
  const start = range?.start ?? null;
  const end = range?.end ?? null;
  useEffect(() => {
    if (start === null || end === null) return;
    setPage((p) => (p >= start && p <= end ? p : start));
  }, [model.id, shown?.digest, start, end]);
  const at = Math.min(page, Math.max(count - 1, 0));

  const go = (n: number) => {
    if (!shown || n < 0 || n >= count) return;
    setPage(n);
    const id = sectionOfPage(shown.sections, n, count);
    if (id && !(ids as string[]).includes(id)) onSection?.(id as PageId);
  };

  return (
    <>
      <header className={styles.bar}>
        <span className={styles.caption}>
          {shown ? (
            <>
              Side {at + 1} af {count}
              {range && range.end > range.start && (
                <span className={styles.subInline}>
                  {' '}
                  · {model.title} s. {range.start + 1}–{range.end + 1}
                </span>
              )}
            </>
          ) : (
            'Rapporten opbygges…'
          )}
        </span>
        {preview.updating && shown && (
          <span className={styles.updating} role="status">
            Opdaterer…
          </span>
        )}
        <Pill tone={model.ready ? 'good' : 'warn'}>{model.ready ? 'Klar' : 'Ufuldstændig'}</Pill>
        {shown && (
          <span className={styles.pager}>
            <IconButton label="Forrige side" onClick={() => go(at - 1)} disabled={at <= 0}>
              ‹
            </IconButton>
            <IconButton label="Næste side" onClick={() => go(at + 1)} disabled={at >= count - 1}>
              ›
            </IconButton>
          </span>
        )}
      </header>
      {!model.ready && model.reasons.length > 0 && (
        <ul className={styles.reasons} aria-label="Mangler på siden">
          {model.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
      {preview.error && (
        <p className={styles.reasons} role="alert">
          Den nyeste ændring kunne ikke opbygges ({preview.error}) — siderne herunder er fra før.{' '}
          <TextLink onClick={preview.refresh}>Prøv igen</TextLink>
        </p>
      )}
      {model.id === 'forside' && cover?.supported && (
        <div className={styles.coverBar}>
          <span className={styles.subInline}>Forsidebillede: {coverCaption(cover.cover)}</span>
          {cover.canEdit && (
            <>
              <TextLink size="sm" onClick={() => setPicking(true)} disabled={cover.busy}>
                Vælg forsidebillede
              </TextLink>
              {cover.cover?.source != null && (
                <TextLink size="sm" onClick={() => void cover.clear()} disabled={cover.busy}>
                  Brug modelbillede
                </TextLink>
              )}
            </>
          )}
        </div>
      )}
      {shown && count > 0 ? (
        <figure className={`${styles.pageFrame} ${preview.updating ? styles.stale : ''}`}>
          <img
            className={styles.pageImage}
            src={api.reportPreviewPageUrl(shown.digest, at, ppi ?? shown.ppi)}
            alt={`Rapportens side ${at + 1} af ${count} (${model.title})`}
            draggable={false}
          />
        </figure>
      ) : (
        <div className={styles.pageFrame}>
          <Spinner label={shown ? 'Rapporten har ingen sider' : 'Opbygger rapporten…'} />
        </div>
      )}
      {picking && cover && <CoverPickerDialog state={cover} onClose={() => setPicking(false)} />}
    </>
  );
}

function DocumentPage({
  model,
  report,
  data,
  project,
}: {
  model: PageModel;
  report: ReportId;
  data: CaseData;
  project: ProjectRecord | null;
}) {
  switch (model.id) {
    case 'forside':
      return <Cover project={project} versions={data.reportVersions} report={report} />;
    case 'sagsdata':
      return (
        <>
          <h1 className={styles.h1}>{report === 'miljoe' ? `Sagsdata · ${REPORT_TITLE.miljoe}` : 'Sagsdata'}</h1>
          <table className={styles.table}>
            <tbody>
              {sagsdataRows(project).map((r) => (
                <tr key={r.label}>
                  <th scope="row" className={styles.rowHead}>
                    {r.label}
                  </th>
                  <td>{r.value ?? <Missing />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );
    case 'ressourcer':
      return (
        <>
          <h1 className={styles.h1}>Ressourcekortlægning</h1>
          <p className={styles.p}>{resourceSummaryLine(data.types, data.summary)}</p>
          <div className={styles.scroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Type</th>
                  <th scope="col">EAK</th>
                  <th scope="col">Behandling</th>
                  <th scope="col" className={styles.num}>
                    Mængde
                  </th>
                  <th scope="col" className={styles.num}>
                    Masse
                  </th>
                </tr>
              </thead>
              <tbody>
                {typeRows(data.types).map((t) => (
                  <tr key={t.id}>
                    <td className="mono">#{t.id}</td>
                    <td>
                      {t.name} {!t.approved && <Missing>ikke godkendt</Missing>}
                    </td>
                    <td className="mono">{t.eak ?? <Missing />}</td>
                    <td>{t.treatment}</td>
                    <td className={`${styles.num} mono`}>{t.quantity}</td>
                    <td className={`${styles.num} mono`}>{t.mass ?? (t.massNeeded ? <Missing /> : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={styles.note}>Kun godkendte mængder indgår i rapportens kortlægningsafsnit.</p>
        </>
      );
    case 'miljoe':
      // The Miljøkortlægning's Prøver og bilag leaf: its samples, then the appendices.
      return (
        <>
          <MiljoeContent data={data} />
          <AppendixContent data={data} nested />
        </>
      );
    case 'bilag':
      return <AppendixContent data={data} />;
    case 'nedrivningsplan': {
      const steps = demolitionSteps(data.types);
      return (
        <>
          <h1 className={styles.h1}>Nedrivningsplan</h1>
          <h2 className={styles.h2}>Rækkefølge</h2>
          {steps.length === 0 ? (
            <p className={styles.p}>Ingen typer i kortlægningen endnu.</p>
          ) : (
            <ol className={styles.steps}>
              {steps.map((s) => (
                <li key={s.title}>
                  <span className={styles.stepTitle}>{s.title}</span>
                  <span className={styles.sub}>{s.items.join(', ')}</span>
                </li>
              ))}
            </ol>
          )}
          <h2 className={styles.h2}>Modtagere</h2>
          <p className={styles.p}>
            <Missing>{RECIPIENTS_NOTE}</Missing>
          </p>
        </>
      );
    }
    default:
      return null;
  }
}

function MiljoeContent({ data }: { data: CaseData }) {
  const rows = sampleRows(data.samples, data.types);
  return (
    <>
      <h1 className={styles.h1}>Miljøkortlægning</h1>
      <p className={styles.p}>Mistanke om miljøfarlige stoffer og udtagne prøver.</p>
      {rows.length === 0 ? (
        <p className={styles.p}>Der er ikke registreret prøver i sagen.</p>
      ) : (
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Prøve</th>
                <th scope="col">Stof / materiale</th>
                <th scope="col">Typer</th>
                <th scope="col">Status</th>
                <th scope="col">Resultat</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td className="mono">{s.code}</td>
                  <td>
                    {s.title}
                    {s.what && <span className={styles.sub}>{s.what}</span>}
                  </td>
                  <td>{s.types ?? <Missing>ingen type</Missing>}</td>
                  <td>{s.stage}</td>
                  <td>
                    {s.result === null ? (
                      <Missing>afventer</Missing>
                    ) : (
                      <Pill tone={s.contaminated ? 'crit' : 'good'}>{s.result}</Pill>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Prøver & bilag; `nested` under the Miljøkortlægning on the same sheet (a section, not a page). */
function AppendixContent({ data, nested }: { data: CaseData; nested?: boolean }) {
  const rows = appendixRows(data.samples, data.photos);
  return (
    <>
      {nested ? <h2 className={styles.h2}>Prøver &amp; bilag</h2> : <h1 className={styles.h1}>Prøver &amp; bilag</h1>}
      <p className={styles.p}>Laboratorierapporterne vedlægges som bilag.</p>
      {!nested && <h2 className={styles.h2}>Bilag</h2>}
      {rows.length === 0 ? (
        <p className={styles.p}>Ingen bilag endnu.</p>
      ) : (
        <ul className={styles.appendix}>
          {rows.map((r) => (
            <li key={r.letter}>
              <span className={`${styles.letter} mono`}>{r.letter}</span> {r.text}{' '}
              {r.status ? <span className={styles.subInline}>· {r.status}</span> : <Missing>afventer</Missing>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Cover({
  project,
  versions,
  report,
}: {
  project: ProjectRecord | null;
  versions: ReportPdfVersion[] | null;
  report: ReportId;
}) {
  const miljoe = report === 'miljoe';
  const cover = coverModel(project, versions, REPORT_TITLE[report]);
  const url = useMemo(() => api.renderUrl(COVER_RENDER), []);
  const [failed, setFailed] = useState(false);
  return (
    <>
      <p className={styles.kicker}>{miljoe ? REPORT_TITLE.miljoe : 'Ressourcekortlægning og nedrivningsplan'}</p>
      <h1 className={styles.h1}>{cover.heading}</h1>
      <p className={styles.p}>{cover.place ?? <Missing>adresse og BBR mangler</Missing>}</p>
      <div className={styles.cover}>
        {failed ? (
          <span className={styles.coverEmpty}>Rendering af bygningen kunne ikke tegnes</span>
        ) : (
          <img className={styles.coverImage} src={url} alt="Bygningen som punktsky, set skråt oppefra" onError={() => setFailed(true)} />
        )}
      </div>
      <p className={styles.p}>
        Udarbejdet af {cover.preparedBy ?? <Missing />}
        <br />
        {/* The stored versions are the Ressourcekortlægning's. */}
        {miljoe ? MILJOE_NOTE : cover.version}
      </p>
      <p className={styles.p}>{cover.legal}</p>
    </>
  );
}

// ------------------------------------------------------------------ PDF --

function PdfSheet({ versions }: { versions: ReportPdfVersion[] | null }) {
  const latest = versions?.[0] ?? null;
  if (versions === null) {
    return (
      <section className={styles.sheet} aria-label="PDF">
        <Spinner label="Indlæser versioner…" />
      </section>
    );
  }
  if (!latest) {
    return (
      <section className={styles.sheet} aria-label="PDF">
        <EmptyState
          title="Ingen versioner endnu"
          detail="Generér den første version i panelet til højre — den gemmes i projektet og vises her."
        />
      </section>
    );
  }
  const url = api.reportPdfUrl(latest.id);
  const title = versionTitle(latest);
  return (
    <section className={`${styles.sheet} ${styles.pdfSheet}`} aria-label={title}>
      <header className={styles.bar}>
        <span className={styles.caption}>Seneste gemte version · {title}</span>
        <TextLink size="sm" href={url} target="_blank" rel="noreferrer">
          Åbn i nyt vindue
        </TextLink>
        <TextLink size="sm" href={url} download={`ressourcekortlaegning-v${latest.version}.pdf`}>
          Hent PDF
        </TextLink>
      </header>
      {/* Keyed on the version: a new one replaces the embed rather than reusing it. */}
      <object key={latest.id} className={styles.pdf} data={url} type="application/pdf" aria-label={title}>
        <p className={styles.p}>
          PDF’en kan ikke vises i browseren her.{' '}
          <TextLink size="sm" href={url} download={`ressourcekortlaegning-v${latest.version}.pdf`}>
            Hent den i stedet
          </TextLink>
          .
        </p>
      </object>
    </section>
  );
}
